<template>
  <div class="app" @touchstart="handleTouchStart" @touchmove="handleTouchMove" @touchend="handleTouchEnd">
    <!-- 下拉刷新提示 -->
    <div class="pull-refresh" :class="{ 'pulling': isPulling, 'refreshing': isRefreshing }">
      <i v-if="isRefreshing" class="fa-solid fa-spinner fa-spin"></i>
      <i v-else class="fa-solid fa-arrow-down"></i>
      <span>{{ isRefreshing ? '刷新中...' : '下拉刷新' }}</span>
    </div>

    <!-- ===== 固定顶部栏（滚动后显示） ===== -->
    <div class="fixed-top" :class="{ show: scrolled }">
      <img class="fixed-logo" src="../assets/logo.png" alt="logo" />
      <router-link to="/search" class="fixed-search">
        <i class="fa-solid fa-magnifying-glass"></i>
        <span>搜索商家、补贴政策或商品</span>
      </router-link>
    </div>

    <!-- ===== 顶部渐变区域 ===== -->
    <div class="hero">
      <!-- 光线扫过效果 -->
      <div class="hero-light-sweep"></div>

      <!-- 飞机动画 -->
      <div class="hero-airplane">
        <img src="../assets/images/feiji.png" alt="飞机" class="airplane-img" />
      </div>

      <!-- 浮动粒子 -->
      <div class="hero-particles">
        <span class="particle"></span>
        <span class="particle"></span>
        <span class="particle"></span>
        <span class="particle"></span>
        <span class="particle"></span>
        <span class="particle"></span>
        <span class="particle"></span>
        <span class="particle"></span>
      </div>

      <!-- 装饰元素 -->
      <div class="hero-decor">
        <div class="decor-circle decor-1"></div>
        <div class="decor-circle decor-2"></div>
        <div class="decor-circle decor-3"></div>
        <i class="fa-solid fa-star decor-star decor-4"></i>
        <i class="fa-solid fa-star decor-star decor-5"></i>
        <i class="fa-solid fa-utensils decor-icon decor-6"></i>
        <i class="fa-solid fa-cart-shopping decor-icon decor-7"></i>
        <i class="fa-solid fa-tv decor-icon decor-8"></i>
      </div>

      <!-- Logo + 标题 + 收藏 -->
      <div class="hero-top">
        <div class="hero-brand">
          <img class="brand-logo" src="../assets/logo.png" alt="logo" />
          <div>
            <h1 class="brand-name">数聚吴川</h1>
            <p class="brand-sub">SHUJU WUCHUAN</p>
          </div>
        </div>
        <FavGuide v-model:visible="showFavGuide" />
      </div>

      <!-- 大标题 -->
      <div class="hero-title">
        <h2>数 聚 吴 川</h2>
        <p>— 云享惠民 · 省钱宝典 —</p>
      </div>

      <!-- 统计条 -->
      <div class="hero-stats">
        <div class="hs-item">
          <i class="fa-solid fa-chart-line hs-ic"></i>
          <span>本周新增 <strong>15</strong> 家商户</span>
        </div>
        <div class="hs-div"></div>
        <div class="hs-item">
          <i class="fa-solid fa-shop hs-ic"></i>
          <span>共有 <strong>{{ totalCount }}</strong> 家参与</span>
        </div>
        <div class="hs-div"></div>
        <div class="hs-item">
          <i class="fa-solid fa-users hs-ic"></i>
          <span>已有 <strong>{{ participantCount }}</strong> 人参与换新</span>
        </div>
      </div>

      <!-- 搜索栏 + 街镇选择器 -->
      <div class="search-row">
        <TownSelect :towns="towns" :activeTown="activeTown" :townCountMap="townCountMap" @select-all="selectAll"
          @select-town="selectTown" @update:activeTown="activeTown = $event" />
        <router-link to="/search" class="hero-search">
          <i class="fa-solid fa-magnifying-glass hs-search-icon"></i>
          <span class="hs-search-text">搜索商家、补贴政策或商品</span>
          <button class="hs-btn" @click.prevent="$router.push('/search')">
            <i class="fa-solid fa-magnifying-glass"></i>
            <span>搜索</span>
          </button>
        </router-link>
      </div>

      <!-- 街镇 Tabs 组件（暂时隐藏） -->
      <!--
      <TownTabs
        :towns="towns"
        :activeTown="activeTown"
        :townCountMap="townCountMap"
        :totalCount="totalCount"
        @select-all="selectAll"
        @select-town="selectTown"
      />
      -->
    </div>

    <!-- ===== 四大模块 ===== -->
    <div class="modules-wrapper">
      <!-- 浮动装饰 -->
      <div class="modules-particles">
        <span class="m-particle"></span>
        <span class="m-particle"></span>
        <span class="m-particle"></span>
        <span class="m-particle"></span>
      </div>
      <div class="modules">
        <!-- 模块2：热门行业入口 -->
        <div class="mod-card" v-if="sortedCategories.length > 0">
          <div class="mod-hd">
            <div class="mod-hd-left">
              <!-- <span class="mod-num">1</span> -->
              <h3 class="mod-tt"><img src="../assets/icon/hot.png" class="mod-icon-img" alt="热门" />热门行业入口</h3>
            </div>
          </div>
          <div class="cat-grid">
            <router-link v-for="c in sortedCategories" :key="c.id"
              :to="`/category/${c.id}?name=${encodeURIComponent(c.name)}`" class="cat-item">
              <div class="cat-circle" :style="{ background: catColors[(c.id - 1) % catColors.length] }">
                <img v-if="c.icon" :src="c.icon" class="cat-icon-img" loading="lazy" />
                <i v-else :class="catFaIcons[c.name] || 'fa-solid fa-box-open'"></i>
              </div>
              <span>{{ c.name }}</span>
            </router-link>
          </div>
        </div>
        <!-- 模块1：补贴专区·大图入口 -->
        <div class="mod-card trade-in-card">
          <router-link to="/category/3?name=电器数码" class="trade-in-banner">
            <img src="../assets/images/yjhx.jpg" alt="补贴专区" class="trade-in-img" loading="lazy" />
            <div class="trade-in-glow"></div>
          </router-link>
        </div>

        <!-- 暑假惠民促销入口 -->
        <div class="mod-card summer-promo-card">
          <router-link to="/policy-list" class="summer-promo-banner">
            <div class="summer-promo-content">
              <div class="summer-promo-text">
                <h3 class="summer-promo-title">国庆惠民促销</h3>
                <p class="summer-promo-sub">限时特惠 · 好礼不停</p>
              </div>
              <div class="summer-promo-badge">
                <span>热卖中</span>
              </div>
            </div>
            <div class="summer-promo-glow"></div>
          </router-link>
        </div>
        <!-- 汽车促销入口 -->
        <div class="mod-card trade-in-card">
          <router-link to="/category/5?name=汽车服务" class="trade-in-banner">
            <img src="../assets/images/car.jpg" alt="补贴专区" class="trade-in-img" loading="lazy" />
            <div class="trade-in-glow"></div>
          </router-link>
        </div>

        <!-- 模块3：月度更新·惠民上新 -->
        <div class="mod-card" v-if="hotProducts.length > 0">
          <div class="mod-card-glow"></div>
          <div class="mod-hd">
            <div class="mod-hd-left">
              <!-- <span class="mod-num">3</span> -->
              <h3 class="mod-tt"><img src="../assets/icon/coupon.png" class="mod-icon-img" alt="优惠" />月度更新 · 惠民上新</h3>
              <span class="mod-badge">{{ currentMonth }}月热推</span>
            </div>
            <router-link :to="`/more-products?townId=${activeTown}`" class="mod-link">更多优惠 <i
                class="fa-solid fa-chevron-right"></i></router-link>
          </div>
          <div class="goods-scroll" ref="productScroll" @mouseenter="pauseProductAutoScroll"
            @mouseleave="resumeProductAutoScroll">
            <router-link v-for="g in hotProducts" :key="g.id" :to="`/product/${g.id}`" class="goods-card">
              <span class="gc-tag" :class="getTagClass(g.tag)">{{ g.tag || '热销' }}</span>
              <div class="gc-img">
                <img :src="(g.image || g._merchantImage) ? imgUrl(g.image || g._merchantImage) : '/no_pic.png'" :alt="g.name" loading="lazy" />
              </div>
              <h4 class="gc-name">{{ g.name }}</h4>
              <p class="gc-price">补贴价 <span>¥ {{ g.subsidy_price }}</span></p>
            </router-link>
          </div>
        </div>

        <!-- 模块4：优质商户推荐 -->
        <div class="mod-card" v-if="recommendMerchants.length > 0">
          <div class="mod-card-glow"></div>
          <div class="mod-hd">
            <div class="mod-hd-left">
              <!-- <span class="mod-num">4</span> -->
              <h3 class="mod-tt"><img src="../assets/icon/recommend.png" class="mod-icon-img" alt="推荐" />热门商户推荐 <span
                  class="mod-tt-sub">({{ currentTownName }})</span></h3>
            </div>
          </div>
          <div class="shop-grid" ref="shopScroll">
            <router-link v-for="m in recommendMerchants" :key="m.id" :to="`/shop/${m.id}`" class="shop-card"
              v-lazy-bg="m.main_image ? imgUrl(m.main_image) : ''">
              <!-- 推荐角标 -->
              <div v-if="m.sort && m.sort >= 100" class="sc-recommend-badge">
                <img src="../assets/icon/recommend-01.png" class="recommend-badge-icon" />
              </div>
              <!-- 优惠角标 -->
              <div v-if="m.coupon" class="sc-coupon-badge">
                <img src="../assets/icon/coupon-tips.png" class="coupon-badge-icon" />
              </div>
              <div class="sc-img" :style="{ background: shopBg(m.category?.name) }">
                <img v-if="m.main_image" :src="imgUrl(m.main_image)" :alt="m.name" loading="lazy" @error="e => handleImgError(e, m)" />
                <div v-else class="sc-img-default">
                  <i :class="shopIcon(m.category?.name)"></i>
                  <span class="sc-img-text">{{ m.name?.substring(0, 2) || '商户' }}</span>
                </div>
                <!-- 分类角标 -->
                <span v-if="m.category?.name" class="sc-cat-badge">{{ m.category.name }}</span>
                <!-- 光线扫过效果 -->
                <div class="sc-img-shine"></div>
              </div>
              <div class="sc-info">
                <div class="sc-name-row">
                  <h4 class="sc-name">{{ m.name }}</h4>
                  <span v-if="m.support_trade_in" class="sc-trade-tag">以旧换新</span>
                </div>
                <div class="sc-meta-row">
                  <span class="sc-loc"><i class="fa-solid fa-location-dot"></i> {{ m.town?.name || '吴川市' }}</span>
                  <!-- <span v-if="m.view_count" class="sc-views"><i class="fa-solid fa-eye"></i> {{ m.view_count > 999 ?
                    (m.view_count / 1000).toFixed(1) + 'k' : m.view_count }}</span> -->
                </div>
                <p v-if="m.address" class="sc-addr"><i class="fa-solid fa-map-pin"></i> {{ m.address }}</p>
                <div v-if="m.coupon" class="sc-coupon">
                  <div class="sc-cpn-row">
                    <span class="sc-cpn">
                      <img src="../assets/icon/coupon.png">
                      <span class="sc-cpn-content">
                        <span v-for="(line, idx) in formatDesc(m.coupon.name || '满' + m.coupon.threshold + '减' + m.coupon.reduce_amount)" :key="idx" class="sc-cpn-line">{{ line }}</span>
                      </span>
                    </span>
                    <!-- <span v-if="m.coupon.remainQuota !== undefined" class="sc-cpn-remain"><i
                        class="fa-solid fa-fire"></i> 余{{ m.coupon.remainQuota }}张</span> -->
                  </div>
                  <div class="sc-cpn-meta">
                    <!-- <span v-if="m.coupon.valid_end" class="sc-cpn-date"><i class="fa-regular fa-calendar"></i> {{
                      m.coupon.valid_end }}</span> -->
                    <span v-if="m.coupon.description" class="sc-cpn-desc">
                      <i class="fa-solid fa-circle-info"></i>
                      <span class="sc-cpn-desc-content">
                        <span v-for="(line, idx) in formatDesc(m.coupon.description)" :key="idx" class="sc-cpn-desc-line">{{ line }}</span>
                      </span>
                    </span>
                  </div>
                </div>
                <div class="sc-bottom">
                  <span class="sc-enter"><i class="fa-solid fa-store"></i> 进店逛逛 <i
                      class="fa-solid fa-chevron-right"></i></span>
                </div>
              </div>
            </router-link>
          </div>
        </div>

      </div>
    </div>

    <!-- ===== 悬浮按钮 ===== -->
    <div class="fab" @click="showShare = true">
      <div class="fab-circle">
        <!-- <i class="fa-solid fa-gift"></i> -->
        <img src="../assets/icon/gifts.png">
      </div>
      <span class="fab-text">分享优惠</span>
    </div>

    <!-- ===== 底部公告 ===== -->
    <div class="notice-fixed" @click="scrollToFooter">
      <div class="notice-bar">
        <div class="nb-left">
          <!-- <i class="fa-solid fa-bullhorn nb-ic"></i> -->
          <img src="../assets/images/gg.png" class="nb-ic">
          <span>免费入驻<span class="nb-highlight">敬请垂询</span> - 点击获取联系方式</span>
        </div>
        <i class="fa-solid fa-chevron-right"></i>
      </div>
    </div>

    <!-- ===== 分享弹窗 ===== -->
    <div v-if="showShare" class="share-mask" @click.self="showShare = false">
      <div class="share-box">
        <div class="share-close" @click="showShare = false">
          <i class="fa-solid fa-xmark"></i>
        </div>
        <h3>分享优惠</h3>
        <p class="share-desc">扫描二维码，分享给好友一起享优惠</p>
        <div class="share-qr">
          <img src="../assets/ewm.png" />
          <!-- <canvas ref="qrCanvas" width="160" height="160"></canvas> -->
        </div>
        <!-- <p class="share-url">{{ shareUrl }}</p> -->
        <button class="share-btn" @click="copyUrl">
          <i class="fa-solid fa-link"></i>
          <span>{{ copied ? '已复制' : '复制链接' }}</span>
        </button>
        <button class="share-btn-wechat" @click="showShareTip = true">
          <i class="fa-brands fa-weixin"></i>
          <span>分享给朋友</span>
        </button>
      </div>
    </div>

    <!-- 分享提示 -->
    <ShareTip v-model:visible="showShareTip" />

    <!-- ===== 底部栏 ===== -->
    <nav class="bottombar">
      <div class="bb-item active" @click="$router.push('/')">
        <i class="fa-solid fa-house-chimney"></i>
        <span>首页</span>
      </div>
      <div class="bb-item" @click="$router.push('/policy-image')">
        <i class="fa-solid fa-tag"></i>
        <span>政策/活动</span>
      </div>
    </nav>

  </div>
</template>

<script>
// import TownTabs from '@/components/TownTabs.vue'
import TownSelect from '@/components/TownSelect.vue'
import ShareTip from '@/components/ShareTip.vue'
import FavGuide from '@/components/FavGuide.vue'
import { initWechat, setShareData } from '../utils/wechat'

// 背景图懒加载指令
const lazyBgObserver = new IntersectionObserver((entries) => {
  entries.forEach(entry => {
    if (entry.isIntersecting) {
      const el = entry.target
      const bg = el.dataset.lazyBg
      if (bg) {
        el.style.backgroundImage = bg
        el.removeAttribute('data-lazy-bg')
        lazyBgObserver.unobserve(el)
      }
    }
  })
}, { rootMargin: '200px' })

const lazyBg = {
  mounted(el, binding) {
    if (binding.value) {
      el.dataset.lazyBg = `url(${binding.value})`
      el.style.backgroundImage = 'none'
      lazyBgObserver.observe(el)
    }
  },
  updated(el, binding) {
    if (binding.value !== binding.oldValue) {
      lazyBgObserver.unobserve(el)
      if (binding.value) {
        el.dataset.lazyBg = `url(${binding.value})`
        el.style.backgroundImage = 'none'
        lazyBgObserver.observe(el)
      }
    }
  },
  unmounted(el) {
    lazyBgObserver.unobserve(el)
  }
}

export default {
  name: 'HomePage',
  components: { /*TownTabs,*/ TownSelect, ShareTip, FavGuide },
  directives: { lazyBg },

  data() {
    return {
      towns: [],
      categories: [],
      categoryCountMap: {},
      hotPolicies: [],
      hotProducts: [],
      recommendMerchants: [],
      activeTown: null,
      currentTownName: '梅录街道',
      showShare: false,
      showShareTip: false,
      showFavGuide: false,
      copied: false,
      scrolled: false,
      currentMonth: new Date().getMonth() + 1,

      catColors: [
        '#3b82f6', '#22d3ee', '#4ade80', '#c084fc', '#fb923c', '#f59e0b'
      ],

      catFaIcons: {
        '餐饮美食': 'fa-solid fa-utensils',
        '商超便利': 'fa-solid fa-cart-shopping',
        '电器数码': 'fa-solid fa-display',
        '家居建材': 'fa-solid fa-couch',
        '汽车服务': 'fa-solid fa-car',
        '美容美发': 'fa-solid fa-scissors',
        '休闲娱乐': 'fa-solid fa-gamepad',
        '医疗健康': 'fa-solid fa-heart-pulse'
      },

      shopColors: {
        '餐饮美食': '#fff7ed',
        '商超便利': '#eff6ff',
        '电器数码': '#eff6ff',
        '家居建材': '#fdf4ff',
        '汽车服务': '#f0fdf4',
        '美容美发': '#fdf2f8',
        '休闲娱乐': '#fefce8',
        'medical': '#fef2f2'
      },

      shopFaIcons: {
        '餐饮美食': 'fa-solid fa-shrimp',
        '商超便利': 'fa-solid fa-store',
        '电器数码': 'fa-solid fa-tv',
        '家居建材': 'fa-solid fa-couch',
        '汽车服务': 'fa-solid fa-car',
        '美容美发': 'fa-solid fa-scissors',
        '休闲娱乐': 'fa-solid fa-gamepad',
        '医疗健康': 'fa-solid fa-heart-pulse'
      },

      townCountMap: {},
      totalCount: 0,

      // 自动滚动相关
      policyAutoScrollTimer: null,
      policyScrollPaused: false,

      // 参与人数自动递增
      participantCount: 3284,
      participantTimer: null,

      // 商品自动滚动
      productAutoScrollTimer: null,
      productScrollPaused: false,

      // 下拉刷新
      isPulling: false,
      isRefreshing: false,
      touchStartY: 0,
      pullThreshold: 80
    }
  },

  computed: {
    shareUrl() {
      return window.location.origin
    },
    sortedCategories() {
      // 过滤掉没有商家的类别，按数量从多到少排序
      return this.categories
      //return this.categories
      //  .filter(cat => (this.categoryCountMap[cat.id] || 0) > 0)
      //  .sort((a, b) => (this.categoryCountMap[b.id] || 0) - (this.categoryCountMap[a.id] || 0))
    }
  },

  watch: {
    showShare(val) {
      if (val) {
        // this.$nextTick(() => this.drawQR())
      }
    }
  },

  mounted() {
    this.fetchData()
    window.addEventListener('scroll', this.handleScroll)
    this.startPolicyAutoScroll()
    this.startParticipantCount()
    this.startProductAutoScroll()
    this.initShare()
  },

  beforeUnmount() {
    window.removeEventListener('scroll', this.handleScroll)
    this.stopPolicyAutoScroll()
    this.stopParticipantCount()
    this.stopProductAutoScroll()
  },

  methods: {
    async initShare() {
      const wx = await initWechat()
      if (wx) {
        setShareData(wx, {
          title: '数聚吴川 - 云享惠民·省钱宝典',
          desc: '数据驱动，惠民到家，发现吴川优质商户和补贴政策',
          link: window.location.href,
          imgUrl: ''
        })
      }
    },

    formatDesc(text) {
      if (!text) return []
      // 把 "1. xxx 2. yyy" 或 "一、xxx 二、yyy" 拆成多行，普通文本保持不变
      return text.split(/(?=(?:\d+\.[^\d]|[一二三四五六七八九十]+、))/).map(s => s.trim()).filter(Boolean)
    },

    async fetchData() {
      try {
        const [t, c, p] = await Promise.all([
          this.$api.get('/api/v1/towns'),
          this.$api.get('/api/v1/categories'),
          this.$api.get('/api/v1/policies/hot')
        ])
        this.towns = t.data.data || []
        this.categories = c.data.data || []
        this.hotPolicies = p.data.data || []

        // 获取每个类别的商户数量
        await this.fetchCategoryCounts()

        // 获取每个街镇的商户数量
        await this.fetchTownCounts()

        // 从 localStorage 恢复上次选择的街镇
        const savedTownId = localStorage.getItem('selectedTownId')
        const savedTownName = localStorage.getItem('selectedTownName')

        if (savedTownId !== null && savedTownName !== null) {
          this.activeTown = Number(savedTownId)
          this.currentTownName = savedTownName
        } else {
          // 默认选中全部
          this.activeTown = 0
          this.currentTownName = '吴川市'
        }

        // 根据选择的街镇加载商户
        if (this.activeTown === 0) {
          await this.fetchAllMerchants()
        } else {
          await this.fetchMerchantsByTown(this.activeTown)
        }
      } catch (e) { console.error(e) }
    },

    // 按sort字段排序商户，权重大的排前面
    sortMerchants(merchants) {
      return merchants.sort((a, b) => {
        const sortA = a.sort || 0
        const sortB = b.sort || 0
        return sortB - sortA
      })
    },

    async fetchTownCounts() {
      try {
        const results = await Promise.all(
          this.towns.map(town =>
            this.$api.get(`/api/v1/merchants?townId=${town.id}&pageSize=1`)
              .then(r => ({ id: town.id, count: r.data.data?.pagination?.total || 0 }))
          )
        )
        const map = {}
        let total = 0
        results.forEach(item => {
          map[item.id] = item.count
          total += item.count
        })
        this.townCountMap = map
        this.totalCount = total
      } catch (e) { console.error(e) }
    },

    async fetchCategoryCounts() {
      try {
        const results = await Promise.all(
          this.categories.map(cat =>
            this.$api.get(`/api/v1/merchants?categoryId=${cat.id}&pageSize=1`)
              .then(r => ({ id: cat.id, count: r.data.data?.pagination?.total || 0 }))
          )
        )
        const map = {}
        results.forEach(item => {
          map[item.id] = item.count
        })
        this.categoryCountMap = map
      } catch (e) { console.error(e) }
    },

    selectAll() {
      this.activeTown = 0
      this.currentTownName = '吴川市'
      // 保存到 localStorage
      localStorage.setItem('selectedTownId', '0')
      localStorage.setItem('selectedTownName', '吴川市')
      this.fetchAllMerchants()
    },

    selectTown(town) {
      this.activeTown = town.id
      this.currentTownName = town.name
      // 保存到 localStorage
      localStorage.setItem('selectedTownId', town.id.toString())
      localStorage.setItem('selectedTownName', town.name)
      this.fetchMerchantsByTown(town.id)
    },

    async fetchAllMerchants() {
      try {
        // 尝试从缓存加载
        const cacheKey = 'homepage_merchants_all'
        const cached = localStorage.getItem(cacheKey)
        if (cached) {
          const cacheData = JSON.parse(cached)
          // 缓存有效期5分钟
          if (Date.now() - cacheData.timestamp < 5 * 60 * 1000) {
            this.recommendMerchants = cacheData.data
            await this.fetchAllProducts()
            return
          }
        }

        // 从服务器获取
        const r = await this.$api.get('/api/v1/merchants/recommend?limit=24')
        this.recommendMerchants = this.sortMerchants(r.data.data || [])

        // 保存到缓存
        localStorage.setItem(cacheKey, JSON.stringify({
          data: this.recommendMerchants,
          timestamp: Date.now()
        }))

        // 获取所有商户的商品，总共18条
        await this.fetchAllProducts()
      } catch (e) { console.error(e) }
    },

    async fetchAllProducts() {
      try {
        const r = await this.$api.get('/api/v1/products/hot?limit=18')
        const products = r.data.data || []
        // 添加商户图片兜底
        this.hotProducts = products.map(p => ({
          ...p,
          _merchantImage: p.merchant?.main_image || ''
        }))
      } catch (e) { console.error(e) }
    },

    async fetchMerchantsByTown(id) {
      try {
        // 尝试从缓存加载
        const cacheKey = `homepage_merchants_town_${id}`
        const cached = localStorage.getItem(cacheKey)
        if (cached) {
          const cacheData = JSON.parse(cached)
          // 缓存有效期5分钟
          if (Date.now() - cacheData.timestamp < 5 * 60 * 1000) {
            this.recommendMerchants = cacheData.data
            await this.fetchAllProducts()
            return
          }
        }

        // 从服务器获取
        const r = await this.$api.get(`/api/v1/merchants?townId=${id}&pageSize=24`)
        this.recommendMerchants = this.sortMerchants(r.data.data?.list || [])

        // 保存到缓存
        localStorage.setItem(cacheKey, JSON.stringify({
          data: this.recommendMerchants,
          timestamp: Date.now()
        }))

        // 获取该街镇所有商户的商品，总共18条
        await this.fetchAllProducts()
      } catch (e) { console.error(e) }
    },

    refreshMerchants() {
      this.fetchMerchantsByTown(this.activeTown)
    },

    // 政策卡片自动滚动
    startPolicyAutoScroll() {
      this.stopPolicyAutoScroll()
      this.policyAutoScrollTimer = setInterval(() => {
        if (!this.policyScrollPaused && this.$refs.policyScroll) {
          this.scrollPolicyCards()
        }
      }, 3000) // 每3秒滚动一次
    },

    stopPolicyAutoScroll() {
      if (this.policyAutoScrollTimer) {
        clearInterval(this.policyAutoScrollTimer)
        this.policyAutoScrollTimer = null
      }
    },

    scrollPolicyCards() {
      const el = this.$refs.policyScroll
      if (!el) return

      const cardWidth = 150 // 140px + 10px gap
      const maxScroll = el.scrollWidth - el.clientWidth

      // 如果已经滚动到末尾，则重置到开头（循环）
      if (el.scrollLeft >= maxScroll - 5) {
        el.scrollTo({ left: 0, behavior: 'smooth' })
      } else {
        el.scrollBy({ left: cardWidth, behavior: 'smooth' })
      }
    },

    pauseAutoScroll() {
      this.policyScrollPaused = true
    },

    resumeAutoScroll() {
      this.policyScrollPaused = false
    },

    // 参与人数自动递增
    startParticipantCount() {
      this.participantTimer = setInterval(() => {
        // 随机增加 1-3 人
        const increment = Math.floor(Math.random() * 3) + 1
        this.participantCount += increment
      }, 5000) // 每5秒增加一次
    },

    stopParticipantCount() {
      if (this.participantTimer) {
        clearInterval(this.participantTimer)
        this.participantTimer = null
      }
    },

    // 商品自动滚动（从右往左）
    startProductAutoScroll() {
      this.stopProductAutoScroll()
      this.productAutoScrollTimer = setInterval(() => {
        if (!this.productScrollPaused && this.$refs.productScroll) {
          this.scrollProductCards()
        }
      }, 3000) // 每3秒滚动一次
    },

    stopProductAutoScroll() {
      if (this.productAutoScrollTimer) {
        clearInterval(this.productAutoScrollTimer)
        this.productAutoScrollTimer = null
      }
    },

    scrollProductCards() {
      const el = this.$refs.productScroll
      if (!el) return

      const cardWidth = 122 // 112px + 10px gap
      const maxScroll = el.scrollWidth - el.clientWidth

      // 如果已经滚动到末尾，则重置到开头（循环）
      if (el.scrollLeft >= maxScroll - 5) {
        el.scrollTo({ left: 0, behavior: 'smooth' })
      } else {
        el.scrollBy({ left: cardWidth, behavior: 'smooth' })
      }
    },

    pauseProductAutoScroll() {
      this.productScrollPaused = true
    },

    resumeProductAutoScroll() {
      this.productScrollPaused = false
    },

    imgUrl(path) {
      if (!path) return ''
      if (path.startsWith('http')) return path
      const base = process.env.VUE_APP_API_BASE_URL || 'http://localhost:3000'
      return base + (path.startsWith('/') ? path : '/' + path)
    },

    goodsIcon(name) {
      if (/电扇|风扇/.test(name)) return 'fa-solid fa-fan'
      if (/月饼/.test(name)) return 'fa-solid fa-gift'
      if (/饭煲|电饭/.test(name)) return 'fa-solid fa-kitchen-set'
      if (/炸锅/.test(name)) return 'fa-solid fa-bowl-rice'
      if (/手机/.test(name)) return 'fa-solid fa-mobile-screen'
      if (/电视/.test(name)) return 'fa-solid fa-tv'
      return 'fa-solid fa-box-open'
    },

    shopIcon(cat) {
      return this.shopFaIcons[cat] || 'fa-solid fa-store'
    },

    shopBg(cat) {
      return this.shopColors[cat] || '#f3f4f6'
    },

    getTagClass(tag) {
      if (!tag) return 'tag-hot'
      if (/新品|上新/.test(tag)) return 'tag-new'
      if (/热销|火爆|热卖/.test(tag)) return 'tag-hot'
      if (/补贴/.test(tag)) return 'tag-subsidy'
      if (/限时|特惠/.test(tag)) return 'tag-limited'
      if (/专属|专享/.test(tag)) return 'tag-exclusive'
      return 'tag-hot'
    },

    getPolicyClass(title) {
      if (!title) return 'policy-default'
      if (/家电|电视|冰箱|洗衣机|空调/.test(title)) return 'policy-appliance'
      if (/汽车|电动车|摩托/.test(title)) return 'policy-car'
      if (/手机|电脑|数码/.test(title)) return 'policy-digital'
      if (/家居|家具|建材/.test(title)) return 'policy-home'
      return 'policy-default'
    },

    getPolicyIcon1(title) {
      if (!title) return 'fa-solid fa-tv'
      if (/家电|电视|冰箱|洗衣机|空调/.test(title)) return 'fa-solid fa-tv'
      if (/汽车|电动车|摩托/.test(title)) return 'fa-solid fa-car'
      if (/手机|电脑|数码/.test(title)) return 'fa-solid fa-mobile-screen'
      if (/家居|家具|建材/.test(title)) return 'fa-solid fa-couch'
      return 'fa-solid fa-tv'
    },

    getPolicyIcon2(title) {
      if (!title) return 'fa-solid fa-refrigerator'
      if (/家电|电视|冰箱|洗衣机|空调/.test(title)) return 'fa-solid fa-refrigerator'
      if (/汽车|电动车|摩托/.test(title)) return 'fa-solid fa-motorcycle'
      if (/手机|电脑|数码/.test(title)) return 'fa-solid fa-laptop'
      if (/家居|家具|建材/.test(title)) return 'fa-solid fa-bed'
      return 'fa-solid fa-refrigerator'
    },

    handleImgError(e, merchant) {
      e.target.style.display = 'none'
      // 找到父容器并添加缺省样式
      const parent = e.target.parentElement
      if (parent && !parent.querySelector('.sc-img-default')) {
        const defaultDiv = document.createElement('div')
        defaultDiv.className = 'sc-img-default'
        defaultDiv.innerHTML = `
          <i class="${this.shopIcon(merchant.category?.name)}"></i>
          <span class="sc-img-text">${merchant.name?.substring(0, 2) || '商户'}</span>
        `
        parent.appendChild(defaultDiv)
      }
    },

    handleScroll() {
      this.scrolled = window.scrollY > 200
    },

    handleTouchStart(e) {
      // 只在页面顶部时启用下拉刷新
      if (window.scrollY === 0) {
        this.touchStartY = e.touches[0].clientY
        this.isPulling = false
      }
    },

    handleTouchMove(e) {
      if (this.touchStartY === 0 || this.isRefreshing) return

      const currentY = e.touches[0].clientY
      const pullDistance = currentY - this.touchStartY

      // 只处理向下拉的手势
      if (pullDistance > 0 && window.scrollY === 0) {
        this.isPulling = pullDistance > this.pullThreshold
      }
    },

    handleTouchEnd() {
      if (this.isPulling && !this.isRefreshing) {
        this.doRefresh()
      }
      this.touchStartY = 0
      this.isPulling = false
    },

    async doRefresh() {
      this.isRefreshing = true
      try {
        // 清除缓存
        localStorage.removeItem('homepage_merchants_all')
        // 清除所有街镇的缓存
        Object.keys(localStorage)
          .filter(key => key.startsWith('homepage_merchants_town_'))
          .forEach(key => localStorage.removeItem(key))

        // 重新获取数据
        await this.fetchData()
      } catch (e) {
        console.error('刷新失败:', e)
      } finally {
        this.isRefreshing = false
      }
    },

    scrollToFooter() {
      // 滚动到页面底部显示联系方式
      window.scrollTo({
        top: document.body.scrollHeight,
        behavior: 'smooth'
      })
    },

    drawQR() {
      const canvas = this.$refs.qrCanvas
      if (!canvas) return
      const ctx = canvas.getContext('2d')
      const size = 200
      const cellSize = 4
      const margin = (size - cellSize * 21) / 2

      ctx.clearRect(0, 0, size, size)
      ctx.fillStyle = '#ffffff'
      ctx.fillRect(0, 0, size, size)

      // 简易二维码生成 - 使用文字编码绘制
      const url = this.shareUrl
      const data = this.generateQRData(url)

      ctx.fillStyle = '#1f2937'
      data.forEach((row, y) => {
        row.forEach((cell, x) => {
          if (cell) {
            ctx.fillRect(margin + x * cellSize, margin + y * cellSize, cellSize, cellSize)
          }
        })
      })

      // 中间 Logo
      const logoSize = 28
      const logoX = (size - logoSize) / 2
      const logoY = (size - logoSize) / 2
      ctx.fillStyle = '#ffffff'
      ctx.fillRect(logoX - 2, logoY - 2, logoSize + 4, logoSize + 4)
      ctx.fillStyle = '#0167CC'
      ctx.beginPath()
      ctx.roundRect(logoX, logoY, logoSize, logoSize, 4)
      ctx.fill()
      ctx.fillStyle = '#ffffff'
      ctx.font = 'bold 14px sans-serif'
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'
      ctx.fillText('数', size / 2, size / 2)
    },

    generateQRData(text) {
      const size = 21
      const data = Array.from({ length: size }, () => Array(size).fill(0))

      // 定位图案
      const drawFinder = (x, y) => {
        for (let i = 0; i < 7; i++) {
          for (let j = 0; j < 7; j++) {
            if (i === 0 || i === 6 || j === 0 || j === 6 ||
              (i >= 2 && i <= 4 && j >= 2 && j <= 4)) {
              data[y + i][x + j] = 1
            }
          }
        }
      }

      drawFinder(0, 0)
      drawFinder(size - 7, 0)
      drawFinder(0, size - 7)

      // 时序图案
      for (let i = 8; i < size - 8; i++) {
        data[6][i] = i % 2 === 0 ? 1 : 0
        data[i][6] = i % 2 === 0 ? 1 : 0
      }

      // 数据编码（简化版）
      const hash = this.simpleHash(text)
      let seed = hash
      for (let y = 0; y < size; y++) {
        for (let x = 0; x < size; x++) {
          if (data[y][x] === 0) {
            seed = (seed * 1103515245 + 12345) & 0x7fffffff
            data[y][x] = seed % 3 === 0 ? 1 : 0
          }
        }
      }

      return data
    },

    simpleHash(str) {
      let hash = 0
      for (let i = 0; i < str.length; i++) {
        hash = ((hash << 5) - hash + str.charCodeAt(i)) | 0
      }
      return Math.abs(hash)
    },

    async copyUrl() {
      try {
        await navigator.clipboard.writeText(this.shareUrl)
        this.copied = true
        setTimeout(() => { this.copied = false }, 2000)
      } catch (e) {
        // fallback
        const input = document.createElement('input')
        input.value = this.shareUrl
        document.body.appendChild(input)
        input.select()
        document.execCommand('copy')
        document.body.removeChild(input)
        this.copied = true
        setTimeout(() => { this.copied = false }, 2000)
      }
    }
  }
}
</script>

<style scoped>
/* ================================================================
   配色 + Reset
   ================================================================ */
@import url('https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.0/css/all.min.css');

*,
*::before,
*::after {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

html {
  -webkit-text-size-adjust: 100%;
}

.app {
  width: 100%;
  min-height: 100vh;
  background: #f4f8fb;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'PingFang SC', 'Microsoft YaHei', sans-serif;
  color: #1f2937;
  /* padding-bottom: calc(50px + env(safe-area-inset-bottom, 0px)); */
  overflow-x: hidden;
  position: relative;
}

/* 下拉刷新样式 */
.pull-refresh {
  position: fixed;
  top: -60px;
  left: 0;
  right: 0;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: #fff;
  font-size: 14px;
  transition: transform .3s ease;
  z-index: 100;
}

.pull-refresh.pulling {
  transform: translateY(60px);
}

.pull-refresh.refreshing {
  transform: translateY(60px);
}

/* ================================================================
   固定顶部栏
   ================================================================ */
.fixed-top {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 150;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  background: linear-gradient(180deg, #0167CC, #3d8fd9);
  transform: translateY(-100%);
  transition: transform .35s cubic-bezier(.4, 0, .2, 1), opacity .35s ease;
  opacity: 0;
  pointer-events: none;
}

.fixed-top.show {
  transform: translateY(0);
  opacity: 1;
  pointer-events: auto;
}

.fixed-logo {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #fff;
  padding: 2px;
  flex-shrink: 0;
  box-shadow: 0 1px 4px rgba(0, 0, 0, .15);
}

.fixed-search {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 6px;
  background: #fff;
  border-radius: 20px;
  padding: 8px 14px;
  text-decoration: none;
  color: #9ca3af;
  font-size: 13px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, .08);
}

.fixed-search i {
  font-size: 13px;
}

/* ================================================================
   Hero — 顶部渐变区域
   ================================================================ */
.hero {
  background: linear-gradient(180deg, #0167CC 10%, #3d8fd9 60%, #f4f8fb 100%);
  padding: 12px 16px 12px;
  position: relative;
  overflow: hidden;
  background-image: url('../assets/images/banner.jpg');
  background-size: auto 100%;
  background-repeat: no-repeat;
  background-position: center;
}

/* 顶部光效 */
.hero::before {
  content: '';
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: radial-gradient(ellipse at 30% 20%,
      rgba(255, 255, 255, 0.15) 0%,
      transparent 50%);
  animation: hero-glow-1 6s ease-in-out infinite;
  pointer-events: none;
  z-index: 1;
}

.hero::after {
  content: '';
  position: absolute;
  top: -30%;
  right: -30%;
  width: 150%;
  height: 150%;
  background: radial-gradient(ellipse at 70% 30%,
      rgba(147, 197, 253, 0.2) 0%,
      transparent 50%);
  animation: hero-glow-2 8s ease-in-out infinite;
  pointer-events: none;
  z-index: 1;
}

@keyframes hero-glow-1 {

  0%,
  100% {
    transform: translate(0, 0) scale(1);
    opacity: 0.6;
  }

  33% {
    transform: translate(10%, 5%) scale(1.1);
    opacity: 1;
  }

  66% {
    transform: translate(-5%, -3%) scale(0.95);
    opacity: 0.8;
  }
}

@keyframes hero-glow-2 {

  0%,
  100% {
    transform: translate(0, 0) rotate(0deg);
    opacity: 0.5;
  }

  50% {
    transform: translate(-10%, 10%) rotate(5deg);
    opacity: 1;
  }
}

/* 光线扫过效果 */
.hero-light-sweep {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(105deg,
      transparent 40%,
      rgba(255, 255, 255, 0.1) 45%,
      rgba(255, 255, 255, 0.2) 50%,
      rgba(255, 255, 255, 0.1) 55%,
      transparent 60%);
  animation: light-sweep 4s ease-in-out infinite;
  pointer-events: none;
  z-index: 2;
}

@keyframes light-sweep {
  0% {
    transform: translateX(-100%);
  }

  60%,
  100% {
    transform: translateX(100%);
  }
}

/* 飞机动画 */
.hero-airplane {
  position: absolute;
  top: 55px;
  right: 10px;
  z-index: 5;
  pointer-events: none;
  animation: airplaneFloat 4s ease-in-out infinite;
}

.airplane-img {
  width: 100px;
  height: auto;
  opacity: 0.5;
  transform: rotate(0deg);
  filter: drop-shadow(0 6px 12px rgba(0, 0, 0, 0.25));
}

@keyframes airplaneFloat {

  0%,
  100% {
    transform: translateY(0) rotate(-20deg);
  }

  50% {
    transform: translateY(-12px) rotate(-18deg);
  }
}

/* 装饰元素容器 */
.hero-decor {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  overflow: hidden;
}

/* 装饰圆圈 */
.decor-circle {
  position: absolute;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.08);
}

.decor-1 {
  width: 120px;
  height: 120px;
  top: -30px;
  right: -20px;
  animation: float 6s ease-in-out infinite;
}

.decor-2 {
  width: 80px;
  height: 80px;
  bottom: 20px;
  left: -20px;
  animation: float 8s ease-in-out infinite reverse;
}

.decor-3 {
  width: 60px;
  height: 60px;
  top: 50%;
  right: 20%;
  animation: float 5s ease-in-out infinite;
}

/* 装饰星星 */
.decor-star {
  position: absolute;
  color: rgba(255, 255, 255, 0.15);
  animation: twinkle 3s ease-in-out infinite;
}

.decor-4 {
  font-size: 16px;
  top: 15px;
  left: 25%;
}

.decor-5 {
  font-size: 12px;
  top: 40px;
  right: 15%;
  animation-delay: 1s;
}

/* 装饰图标 */
.decor-icon {
  position: absolute;
  color: rgba(255, 255, 255, 0.1);
  font-size: 24px;
  animation: float 7s ease-in-out infinite;
}

.decor-6 {
  bottom: 40px;
  right: 10%;
  animation-delay: 0.5s;
}

.decor-7 {
  top: 60px;
  left: 10%;
  animation-delay: 1.5s;
}

.decor-8 {
  bottom: 60px;
  left: 30%;
  animation-delay: 2s;
}

/* 浮动动画 */
@keyframes float {

  0%,
  100% {
    transform: translateY(0);
  }

  50% {
    transform: translateY(-10px);
  }
}

/* 闪烁动画 */
@keyframes twinkle {

  0%,
  100% {
    opacity: 0.15;
  }

  50% {
    opacity: 0.4;
  }
}

/* 浮动粒子 */
.hero-particles {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
  pointer-events: none;
  z-index: 1;
}

.particle {
  position: absolute;
  width: 6px;
  height: 6px;
  background: rgba(255, 255, 255, 0.4);
  border-radius: 50%;
  animation: particleFloat 8s ease-in-out infinite;
}

.particle:nth-child(1) {
  left: 10%;
  top: 20%;
  animation-delay: 0s;
  animation-duration: 7s;
}

.particle:nth-child(2) {
  left: 20%;
  top: 60%;
  animation-delay: 1s;
  animation-duration: 9s;
}

.particle:nth-child(3) {
  left: 35%;
  top: 30%;
  animation-delay: 2s;
  animation-duration: 6s;
}

.particle:nth-child(4) {
  left: 50%;
  top: 70%;
  animation-delay: 0.5s;
  animation-duration: 8s;
}

.particle:nth-child(5) {
  left: 65%;
  top: 40%;
  animation-delay: 1.5s;
  animation-duration: 10s;
}

.particle:nth-child(6) {
  left: 75%;
  top: 80%;
  animation-delay: 2.5s;
  animation-duration: 7s;
}

.particle:nth-child(7) {
  left: 85%;
  top: 25%;
  animation-delay: 3s;
  animation-duration: 9s;
}

.particle:nth-child(8) {
  left: 90%;
  top: 55%;
  animation-delay: 0.8s;
  animation-duration: 6s;
}

@keyframes particleFloat {

  0%,
  100% {
    transform: translateY(0) translateX(0) scale(1);
    opacity: 0.4;
  }

  25% {
    transform: translateY(-20px) translateX(10px) scale(1.2);
    opacity: 0.7;
  }

  50% {
    transform: translateY(-10px) translateX(-5px) scale(0.8);
    opacity: 0.5;
  }

  75% {
    transform: translateY(-30px) translateX(15px) scale(1.1);
    opacity: 0.6;
  }
}

.hero-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.hero-brand {
  display: flex;
  align-items: center;
  gap: 8px;
}

.brand-logo {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  /* background: #fff; */
  padding: 2px;
  /* box-shadow: 0 1px 4px rgba(0,0,0,.1); */
}

.brand-name {
  font-size: 18px;
  font-weight: 900;
  color: #000;
  letter-spacing: 1px;
  line-height: 1.1;
}

.brand-sub {
  font-size: 10px;
  color: rgba(0, 0, 0, 0.8);
  margin-top: 1px;
  font-weight: 500;
}

.hero-loc {
  display: flex;
  align-items: center;
  gap: 4px;
  background: rgba(255, 255, 255, .2);
  backdrop-filter: blur(8px);
  color: #fff;
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 12px;
  cursor: pointer;
}

.hero-loc i:first-child {
  font-size: 10px;
}

.hero-loc i:last-child {
  font-size: 8px;
}

/* 大标题 */
.hero-title {
  text-align: center;
  margin: 20px 0 16px;
  visibility: hidden;
}

.hero-title h2 {
  font-size: 24px;
  font-weight: 900;
  color: #fff;
  letter-spacing: 6px;
}

.hero-title p {
  font-size: 12px;
  color: #f1f1f1;
  letter-spacing: 2px;
  font-weight: 500;
  margin-top: 4px;
}

/* 统计条 */
.hero-stats {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: rgba(255, 255, 255, .9);
  backdrop-filter: blur(8px);
  border-radius: 12px;
  padding: 10px 12px;
  margin-bottom: 16px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, .05);
  visibility: hidden;
}

.hs-item {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  color: #6b7280;
}

.hs-ic {
  color: #0167CC;
  font-size: 11px;
}

.hs-item strong {
  font-size: 14px;
  font-weight: 700;
  color: #014d99;
  text-shadow: 0 0 8px rgba(1, 103, 204, 0.3);
  animation: numberPulse 3s ease-in-out infinite;
}

@keyframes numberPulse {

  0%,
  100% {
    text-shadow: 0 0 8px rgba(1, 103, 204, 0.3);
  }

  50% {
    text-shadow: 0 0 12px rgba(1, 103, 204, 0.5), 0 0 20px rgba(1, 103, 204, 0.2);
  }
}

.hs-div {
  width: 1px;
  height: 16px;
  background: #d1d5db;
  flex-shrink: 0;
}

/* 搜索行（选择器 + 搜索框 融为一体） */
.search-row {
  display: flex;
  align-items: center;
  background: #fff;
  border-radius: 24px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, .06);
  height: 40px;
  width: 80%;
}

/* 搜索栏 */
.hero-search {
  display: flex;
  align-items: center;
  gap: 8px;
  background: transparent;
  border: none;
  border-radius: 0;
  padding: 0 5px 0 8px;
  height: 40px;
  flex: 1;
  min-width: 0;
  box-shadow: none;
  text-decoration: none;
  color: inherit;
}

.hs-search-icon {
  color: #9ca3af;
  font-size: 14px;
  flex-shrink: 0;
}

.hs-search-text {
  flex: 1;
  font-size: 14px;
  color: #9ca3af;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.hs-btn {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 0 14px;
  height: 32px;
  border: none;
  border-radius: 18px;
  background: linear-gradient(135deg, #0167CC, #3b82f6);
  color: #fff;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(1, 103, 204, .3);
}

.hs-btn:active {
  background: linear-gradient(135deg, #014d99, #2563eb);
}

.hs-btn i {
  font-size: 12px;
}

/* ================================================================
   模块区
   ================================================================ */
.modules-wrapper {
  background: linear-gradient(180deg, #9cc7fc 0%, #e6effd 60%, #f4f8fb 100%);
  padding: 12px 5px 20px;
  position: relative;
  z-index: 10;
  overflow: hidden;
}

.modules {
  display: flex;
  flex-direction: column;
  gap: 12px;
  background: #fff;
  border-radius: 16px;
  padding: 5px;
  position: relative;
}

.modules-wrapper::before {
  content: '';
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: radial-gradient(circle at 30% 40%, rgba(255, 255, 255, .6) 0%, transparent 40%),
    radial-gradient(circle at 70% 60%, rgba(156, 199, 252, .4) 0%, transparent 35%);
  animation: modulesGlow 8s ease-in-out infinite;
  pointer-events: none;
  z-index: 0;
}

.modules-wrapper>* {
  position: relative;
  z-index: 1;
}

/* 模块区浮动粒子 */
.modules-particles {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
  pointer-events: none;
  z-index: 0;
}

.m-particle {
  position: absolute;
  width: 100px;
  height: 100px;
  background: radial-gradient(circle, rgba(255, 255, 255, 0.4) 0%, transparent 70%);
  border-radius: 50%;
  animation: mParticleFloat 10s ease-in-out infinite;
}

.m-particle:nth-child(1) {
  left: 5%;
  top: 10%;
  animation-delay: 0s;
}

.m-particle:nth-child(2) {
  left: 60%;
  top: 30%;
  animation-delay: 2s;
}

.m-particle:nth-child(3) {
  left: 20%;
  top: 70%;
  animation-delay: 4s;
}

.m-particle:nth-child(4) {
  left: 80%;
  top: 80%;
  animation-delay: 1s;
}

@keyframes mParticleFloat {

  0%,
  100% {
    transform: translateY(0) scale(1);
    opacity: 0.3;
  }

  50% {
    transform: translateY(-20px) scale(1.2);
    opacity: 0.6;
  }
}

@keyframes modulesGlow {

  0%,
  100% {
    transform: translate(0, 0) scale(1);
    opacity: 0.8;
  }

  50% {
    transform: translate(5%, 3%) scale(1.05);
    opacity: 1;
  }
}

/* ---------- 卡片容器 ---------- */
.mod-card {
  background: #fff;
  border-radius: 20px;
  padding: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, .03),
    0 8px 24px rgba(0, 0, 0, .04);
  position: relative;
  overflow: hidden;
  transition: transform .3s ease, box-shadow .3s ease;
}

.mod-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, .05),
    0 12px 32px rgba(0, 0, 0, .08);
}

/* ---------- 模块头部 ---------- */
.mod-hd {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.mod-hd-left {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.mod-num {
  width: 20px;
  height: 20px;
  border-radius: 6px;
  background: #0167CC;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 700;
  flex-shrink: 0;
}

.mod-tt {
  font-size: 16px;
  font-weight: 700;
  color: #0b69fc;
  display: flex;
  align-items: center;
  gap: 8px;
  background: linear-gradient(90deg, #0b69fc, #3b82f6, #0b69fc);
  background-size: 200% 100%;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  animation: titleShimmer 3s ease-in-out infinite;
}

@keyframes titleShimmer {
  0% {
    background-position: 0% 50%;
  }

  50% {
    background-position: 100% 50%;
  }

  100% {
    background-position: 0% 50%;
  }
}

.mod-tt::before {
  content: '';
  width: 4px;
  height: 18px;
  background: linear-gradient(180deg, #0167CC, #60a5fa);
  border-radius: 2px;
  flex-shrink: 0;
}

.mod-tt i {
  background: linear-gradient(135deg, #0167CC, #60a5fa, #93c5fd);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

/* 模块标题图片图标 */
.mod-icon-img {
  width: 18px;
  height: 18px;
  object-fit: contain;
  flex-shrink: 0;
  transition: transform 0.3s ease;
}

.mod-card:hover .mod-icon-img {
  transform: scale(1.15);
}

/* 图标动效基础 */
.icon-animated {
  font-size: 18px;
  transition: transform 0.3s ease;
}

.mod-card:hover .icon-animated {
  transform: scale(1.2);
}

/* 火焰图标 - 跳动 + 颜色 + 发光 */
.icon-fire {
  background: linear-gradient(135deg, #f97316, #fb923c, #fbbf24);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  animation: fire-dance 1.5s ease-in-out infinite, fireGlow 2s ease-in-out infinite;
}

@keyframes fireGlow {

  0%,
  100% {
    filter: drop-shadow(0 0 6px rgba(249, 115, 22, 0.5));
  }

  50% {
    filter: drop-shadow(0 0 12px rgba(249, 115, 22, 0.8)) drop-shadow(0 0 20px rgba(249, 115, 22, 0.3));
  }
}

@keyframes fire-dance {

  0%,
  100% {
    transform: translateY(0) scale(1);
    filter: brightness(1);
  }

  25% {
    transform: translateY(-3px) scale(1.1);
    filter: brightness(1.2);
  }

  50% {
    transform: translateY(-1px) scale(1.05);
    filter: brightness(1.1);
  }

  75% {
    transform: translateY(-4px) scale(1.15);
    filter: brightness(1.3);
  }
}

/* 闪电图标 - 闪烁 + 颜色 */
.icon-bolt {
  background: linear-gradient(135deg, #f59e0b, #fbbf24, #fde68a);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  animation: bolt-flash 2s ease-in-out infinite;
}

@keyframes bolt-flash {

  0%,
  100% {
    opacity: 1;
    transform: scale(1);
  }

  10% {
    opacity: 0.4;
    transform: scale(0.9);
  }

  20% {
    opacity: 1;
    transform: scale(1.2);
  }

  30% {
    opacity: 0.7;
    transform: scale(1);
  }

  40%,
  90% {
    opacity: 1;
    transform: scale(1);
  }
}

/* 店铺图标 - 摇摆 + 颜色 */
.icon-store {
  background: linear-gradient(135deg, #8b5cf6, #a78bfa, #c4b5fd);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  animation: store-swing 3s ease-in-out infinite;
  transform-origin: top center;
}

@keyframes store-swing {

  0%,
  100% {
    transform: rotate(0deg);
  }

  25% {
    transform: rotate(8deg);
  }

  75% {
    transform: rotate(-8deg);
  }
}

.mod-tt-sub {
  font-size: 13px;
  font-weight: 500;
  color: #0167CC;
  margin-left: 4px;
}

.mod-badge {
  font-size: 11px;
  font-weight: 700;
  color: #fff;
  background: linear-gradient(135deg, #ff6b35, #f7931e);
  padding: 4px 12px;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(255, 107, 53, 0.3);
  animation: badgePulse 2s ease-in-out infinite;
}

@keyframes badgePulse {

  0%,
  100% {
    box-shadow: 0 2px 8px rgba(255, 107, 53, 0.3);
  }

  50% {
    box-shadow: 0 2px 12px rgba(255, 107, 53, 0.5), 0 0 20px rgba(255, 107, 53, 0.2);
  }
}

.mod-link {
  font-size: 12px;
  color: #9ca3af;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 2px;
  white-space: nowrap;
  text-decoration: none;
}

.mod-link:active {
  color: #6b7280;
}

.mod-link i {
  font-size: 9px;
}

.mod-sort {
  font-size: 12px;
  color: #0167CC;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
}

.mod-sort i {
  font-size: 10px;
}

/* ================================================================
   模块1 — 政策卡片 (3列网格)
   ================================================================ */
.policy-grid {
  display: flex;
  gap: 10px;
  overflow-x: auto;
  overflow-y: hidden;
  padding-bottom: 4px;
  -webkit-overflow-scrolling: auto;
  scroll-snap-type: x mandatory;
  scroll-behavior: smooth;
  will-change: scroll-position;
}

.policy-grid::-webkit-scrollbar {
  display: none;
}

.policy-card {
  flex: 0 0 140px;
  border: 1px solid rgba(255, 255, 255, .6);
  background: linear-gradient(145deg, rgba(255, 255, 255, .95), rgba(240, 248, 255, .9));
  border-radius: 16px;
  padding: 12px 10px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  text-align: center;
  position: relative;
  overflow: hidden;
  min-height: 180px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, .04),
    inset 0 1px 0 rgba(255, 255, 255, .8);
}

.policy-card::before {
  content: '';
  position: absolute;
  top: -20px;
  right: -20px;
  width: 60px;
  height: 60px;
  background: radial-gradient(circle, rgba(59, 130, 246, .15) 0%, transparent 70%);
  border-radius: 50%;
  pointer-events: none;
}

.policy-card::after {
  content: '';
  position: absolute;
  bottom: -15px;
  left: -15px;
  width: 50px;
  height: 50px;
  background: radial-gradient(circle, rgba(16, 185, 129, .12) 0%, transparent 70%);
  border-radius: 50%;
  pointer-events: none;
}

.pc-title {
  font-size: 12px;
  font-weight: 700;
  color: #1f2937;
}

.pc-sub {
  font-size: 8px;
  color: #9ca3af;
  margin-top: 2px;
  white-space: nowrap;
  transform: scale(.9);
}

.pc-icon-area {
  position: relative;
  width: 100%;
  height: 60px;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  margin: 8px 0;
}

.pc-icon-big {
  font-size: 24px;
  color: #d1d5db;
  position: absolute;
  left: 4px;
  bottom: 2px;
}

.pc-icon-huge {
  font-size: 32px;
  color: #9ca3af;
  position: absolute;
  right: 4px;
  bottom: 0;
}

.pc-badge {
  position: absolute;
  bottom: 0;
  right: 0;
  background: #f97316;
  color: #fff;
  font-size: 8px;
  padding: 3px 6px;
  border-radius: 6px 0 8px 0;
  line-height: 1.3;
  text-align: center;
}

.pc-badge strong {
  font-size: 12px;
}

/* 政策类型样式 */
.policy-appliance .pc-icon-big {
  color: #3b82f6;
}

.policy-appliance .pc-icon-huge {
  color: #2563eb;
}

.policy-appliance {
  border-color: #dbeafe;
  background: rgba(59, 130, 246, .08);
}

.policy-car .pc-icon-big {
  color: #10b981;
}

.policy-car .pc-icon-huge {
  color: #059669;
}

.policy-car {
  border-color: #d1fae5;
  background: rgba(16, 185, 129, .08);
}

.policy-digital .pc-icon-big {
  color: #8b5cf6;
}

.policy-digital .pc-icon-huge {
  color: #7c3aed;
}

.policy-digital {
  border-color: #ede9fe;
  background: rgba(139, 92, 246, .08);
}

.policy-home .pc-icon-big {
  color: #f59e0b;
}

.policy-home .pc-icon-huge {
  color: #d97706;
}

.policy-home {
  border-color: #fef3c7;
  background: rgba(245, 158, 11, .08);
}

.policy-default .pc-icon-big {
  color: #d1d5db;
}

.policy-default .pc-icon-huge {
  color: #9ca3af;
}

.pc-btn {
  width: 100%;
  padding: 6px 0;
  border: none;
  border-radius: 20px;
  background: linear-gradient(135deg, #0167CC, #3b82f6, #60a5fa);
  color: #fff;
  font-size: 10px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 3px;
  position: relative;
  z-index: 1;
  box-shadow: 0 2px 8px rgba(1, 103, 204, .25);
  transition: transform .2s, box-shadow .2s;
}

.pc-btn:active {
  background: linear-gradient(135deg, #014d99, #2563eb);
  transform: scale(.97);
  box-shadow: 0 1px 4px rgba(1, 103, 204, .3);
}

.pc-btn i {
  font-size: 7px;
}

/* ================================================================
   模块1 — 以旧换新大图入口
   ================================================================ */
.trade-in-card {
  padding: 0 !important;
  overflow: hidden;
}

.trade-in-banner {
  display: block;
  position: relative;
  width: 100%;
  border-radius: 12px;
  overflow: hidden;
  text-decoration: none;
  cursor: pointer;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.trade-in-banner:hover {
  transform: translateY(-3px);
  box-shadow: 0 12px 32px rgba(1, 103, 204, 0.25);
}

.trade-in-banner:active {
  transform: scale(0.98);
}

.trade-in-img {
  width: 100%;
  display: block;
  transition: transform 0.5s ease;
}

.trade-in-banner:hover .trade-in-img {
  transform: scale(1.03);
}

/* 光晕效果 */
.trade-in-glow {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  background: radial-gradient(ellipse at 30% 50%,
      rgba(255, 200, 50, 0.15) 0%,
      transparent 60%);
  animation: glow-pulse 3s ease-in-out infinite;
}

@keyframes glow-pulse {

  0%,
  100% {
    opacity: 0.6;
    background: radial-gradient(ellipse at 30% 50%,
        rgba(255, 200, 50, 0.15) 0%,
        transparent 60%);
  }

  50% {
    opacity: 1;
    background: radial-gradient(ellipse at 70% 50%,
        rgba(255, 200, 50, 0.2) 0%,
        transparent 60%);
  }
}

/* 光效扫过 */
.trade-in-banner::after {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 60%;
  height: 100%;
  background: linear-gradient(90deg,
      transparent,
      rgba(255, 255, 255, 0.2),
      transparent);
  animation: shine 4s ease-in-out infinite;
}

@keyframes shine {
  0% {
    left: -100%;
  }

  40%,
  100% {
    left: 100%;
  }
}

/* ================================================================
   暑假惠民促销入口
   ================================================================ */
.summer-promo-card {
  padding: 0 !important;
  overflow: hidden;
}

.summer-promo-banner {
  display: block;
  position: relative;
  width: 100%;
  border-radius: 12px;
  overflow: hidden;
  text-decoration: none;
  cursor: pointer;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  background: linear-gradient(135deg, #ff6b35 0%, #f7931e 50%, #ffcc02 100%);
  min-height: 100px;
}

.summer-promo-banner:hover {
  transform: translateY(-3px);
  box-shadow: 0 12px 32px rgba(255, 107, 53, 0.3);
}

.summer-promo-banner:active {
  transform: scale(0.98);
}

.summer-promo-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  position: relative;
  z-index: 2;
}

.summer-promo-text {
  flex: 1;
}

.summer-promo-title {
  font-size: 22px;
  font-weight: 900;
  color: #fff;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  letter-spacing: 2px;
}

.summer-promo-sub {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.9);
  margin-top: 4px;
  font-weight: 500;
}

.summer-promo-badge {
  background: linear-gradient(135deg, #ef4444, #dc2626);
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  padding: 6px 14px;
  border-radius: 20px;
  box-shadow: 0 2px 8px rgba(239, 68, 68, 0.4);
  animation: badgePulse 2s ease-in-out infinite;
}

.summer-promo-glow {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  background: radial-gradient(ellipse at 30% 50%,
      rgba(255, 255, 255, 0.2) 0%,
      transparent 60%);
  animation: glow-pulse 3s ease-in-out infinite;
}

.summer-promo-banner::after {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 60%;
  height: 100%;
  background: linear-gradient(90deg,
      transparent,
      rgba(255, 255, 255, 0.3),
      transparent);
  animation: shine 4s ease-in-out infinite;
}

/* ================================================================
   模块2 — 行业分类 (6列)
   ================================================================ */
.cat-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px 0;
}

.cat-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  text-decoration: none;
  color: inherit;
}

.cat-item:active {
  transform: scale(.95);
}

.cat-circle {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 18px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, .12),
    inset 0 -2px 4px rgba(0, 0, 0, .15),
    inset 0 2px 4px rgba(255, 255, 255, .3);
  position: relative;
  overflow: hidden;
  transition: transform .3s ease, box-shadow .3s ease;
}

.cat-item:hover .cat-circle {
  transform: scale(1.15) rotate(5deg);
  box-shadow: 0 4px 16px rgba(0, 0, 0, .2),
    0 0 20px currentColor;
}

.cat-circle::before {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  width: 100%;
  height: 100%;
  background: inherit;
  border-radius: 50%;
  transform: translate(-50%, -50%) scale(0);
  opacity: 0;
  z-index: -1;
  transition: transform .4s ease, opacity .4s ease;
}

.cat-item:hover .cat-circle::before {
  transform: translate(-50%, -50%) scale(1.8);
  opacity: 0.3;
}

.cat-icon-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 50%;
}

.cat-circle::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border-radius: 50%;
  background: radial-gradient(circle at 30% 30%, rgba(255, 255, 255, .5) 0%, transparent 50%);
  pointer-events: none;
}

.cat-item span {
  font-size: 11px;
  color: #4b5563;
  font-weight: 500;
}

/* ================================================================
   模块3 — 商品卡片 (横向滚动)
   ================================================================ */
.goods-scroll {
  display: flex;
  gap: 12px;
  overflow-x: auto;
  overflow-y: hidden;
  padding-bottom: 4px;
  -webkit-overflow-scrolling: auto;
  scroll-snap-type: x mandatory;
  scroll-behavior: smooth;
  will-change: scroll-position;
  -webkit-flex-shrink: 0;
}

.goods-scroll::-webkit-scrollbar {
  display: none;
}

.goods-card {
  display: block;
  min-width: 112px;
  width: 112px;
  flex-shrink: 0;
  -webkit-flex-shrink: 0;
  background: #f9fafb;
  border-radius: 12px;
  padding: 8px;
  position: relative;
  text-decoration: none;
  color: inherit;
  transition: transform .3s ease, box-shadow .3s ease;
  scroll-snap-align: start;
}

.goods-card:hover {
  transform: translateY(-6px) scale(1.02);
  box-shadow: 0 8px 24px rgba(0, 0, 0, .12);
}

.goods-card:active {
  transform: scale(0.96);
}

.gc-tag {
  position: absolute;
  top: 0;
  left: 0;
  color: #fff;
  font-size: 10px;
  padding: 3px 8px;
  border-radius: 10px 0 8px 0;
  z-index: 1;
  font-weight: 600;
  animation: tagPulse 2s ease-in-out infinite;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

@keyframes tagPulse {

  0%,
  100% {
    transform: scale(1);
  }

  50% {
    transform: scale(1.05);
  }
}

/* 热销标签 - 红色 */
.tag-hot {
  background: #ef4444;
}

/* 新品标签 - 绿色 */
.tag-new {
  background: #22c55e;
}

/* 补贴标签 - 橙色 */
.tag-subsidy {
  background: #f97316;
}

/* 限时标签 - 紫色 */
.tag-limited {
  background: linear-gradient(135deg, #a855f7, #8b5cf6);
}

/* 专属标签 - 金色 */
.tag-exclusive {
  background: linear-gradient(135deg, #f59e0b, #d97706);
}

.gc-img {
  height: 76px;
  background: #fff;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 8px;
  overflow: hidden;
}

.gc-img img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.gc-img i {
  font-size: 32px;
  color: #d1d5db;
}

.gc-name {
  font-size: 12px;
  font-weight: 700;
  color: #1f2937;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.gc-price {
  font-size: 12px;
  color: #ef4444;
  font-weight: 700;
  margin-top: 6px;
  background: linear-gradient(135deg, #fff5f5, #fef2f2);
  padding: 4px 8px;
  border-radius: 6px;
  border: 1px solid #fecaca;
}

.gc-price span {
  font-size: 16px;
  font-weight: 900;
  text-shadow: 0 0 8px rgba(239, 68, 68, 0.4);
  animation: priceGlow 2s ease-in-out infinite;
}

@keyframes priceGlow {

  0%,
  100% {
    text-shadow: 0 0 8px rgba(239, 68, 68, 0.4);
  }

  50% {
    text-shadow: 0 0 16px rgba(239, 68, 68, 0.6), 0 0 24px rgba(239, 68, 68, 0.3);
  }
}

.gc-sold {
  font-size: 9px;
  color: #9ca3af;
  margin-top: 2px;
}

/* ================================================================
   模块4 — 商户网格 (1列)
   ================================================================ */
.shop-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
  position: relative;
}

.shop-card {
  display: flex;
  gap: 12px;
  border: 1px solid #f0f0f0;
  border-radius: 14px;
  padding: 10px;
  background: #fff;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  transition: transform .3s ease, box-shadow .3s ease, border-color .3s ease;
  text-decoration: none;
  color: inherit;
  position: relative;
  overflow: hidden;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
}

/* 背景图半透明遮罩 */
.shop-card::before {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(255, 255, 255, 0.98) 100%);
  z-index: 0;
  pointer-events: none;
}

/* 确保内容在遮罩之上 */
.shop-card>* {
  position: relative;
  z-index: 1;
}

.shop-card::after {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 50%;
  height: 100%;
  background: linear-gradient(90deg,
      transparent,
      rgba(255, 255, 255, 0.4),
      transparent);
  transition: left .5s ease;
  z-index: 1;
}

.shop-card:hover::after {
  left: 100%;
}

.shop-card:active {
  transform: scale(.98);
}

.sc-img {
  width: 90px;
  height: 90px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  flex-shrink: 0;
  position: relative;
}

.sc-img img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.sc-img i {
  font-size: 28px;
  opacity: .4;
}

.sc-img-default {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  color: #9ca3af;
}

.sc-img-default i {
  font-size: 24px;
  color: #0167CC;
  opacity: 0.6;
  margin-bottom: 4px;
}

.sc-img-text {
  font-size: 10px;
  font-weight: 600;
  color: #6b7280;
  max-width: 90%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* 商户图片光线扫过效果 */
.sc-img-shine {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(105deg,
      transparent 30%,
      rgba(255, 255, 255, 0.3) 45%,
      rgba(255, 255, 255, 0.5) 50%,
      rgba(255, 255, 255, 0.3) 55%,
      transparent 70%);
  transform: translateX(-100%);
  pointer-events: none;
}

.shop-card:hover .sc-img-shine {
  animation: shop-shine 0.8s ease-in-out;
}

@keyframes shop-shine {
  0% {
    transform: translateX(-100%);
  }

  100% {
    transform: translateX(100%);
  }
}

.sc-name {
  font-size: 14px;
  font-weight: 700;
  color: #1f2937;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.3;
  flex: 1;
  min-width: 0;
}

.sc-name-row {
  display: flex;
  align-items: center;
  gap: 6px;
}

.sc-trade-tag {
  font-size: 9px;
  color: #f59e0b;
  background: #fffbeb;
  border: 1px solid #fde68a;
  padding: 1px 5px;
  border-radius: 4px;
  flex-shrink: 0;
  font-weight: 600;
  white-space: nowrap;
}

.sc-meta-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.sc-views {
  font-size: 10px;
  color: #9ca3af;
  display: inline-flex;
  align-items: center;
  gap: 3px;
}

.sc-views i {
  font-size: 9px;
  color: #60a5fa;
}

.sc-addr {
  font-size: 10px;
  color: #9ca3af;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.4;
}

.sc-addr i {
  font-size: 9px;
  color: #d1d5db;
  margin-right: 3px;
}

.sc-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 5px;
  min-width: 0;
  padding: 2px 0;
}

.sc-loc {
  font-size: 10px;
  color: #6b7280;
  display: inline-flex;
  align-items: center;
  gap: 3px;
  background: #f0f7ff;
  padding: 2px 7px;
  border-radius: 10px;
  width: fit-content;
}

.sc-loc i {
  color: #0167CC;
  font-size: 9px;
}

.sc-coupon {
  background: linear-gradient(135deg, #fff1f2 0%, #ffe4e6 100%);
  border: 1px solid #fda4af;
  border-radius: 10px;
  padding: 8px 10px;
  position: relative;
  overflow: hidden;
}

.sc-cpn-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
}

.sc-cpn {
  display: inline-flex;
  align-items: flex-start;
  gap: 4px;
  color: #dc2626;
  font-size: 13px;
  font-weight: 700;
  line-height: 1.45;
  text-align: left;
}

.sc-cpn-content {
  display: flex;
  flex-direction: column;
}

.sc-cpn-line {
  display: block;
  text-align: left;
}

.sc-cpn img {
  width: 16px;
  height: 16px;
  object-fit: contain;
}

.sc-cpn i {
  font-size: 10px;
}

.sc-cpn-remain {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-size: 9px;
  color: #f97316;
  font-weight: 600;
  white-space: nowrap;
}

.sc-cpn-remain i {
  font-size: 9px;
}

.sc-cpn-meta {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 3px;
}

.sc-cpn-date,
.sc-cpn-desc {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-size: 9px;
  color: #9ca3af;
}

.sc-cpn-date i {
  color: #60a5fa;
  font-size: 8px;
}

.sc-cpn-desc {
  align-items: flex-start;
  line-height: 1.45;
  text-align: left;
}

.sc-cpn-desc-content {
  display: flex;
  flex-direction: column;
}

.sc-cpn-desc-line {
  display: block;
  text-align: left;
}

.sc-cpn-desc i {
  font-size: 8px;
  flex-shrink: 0;
  color: #d1d5db;
  margin-top: 1.5px;
}

.sc-bottom {
  display: flex;
  justify-content: flex-end;
  margin-top: 2px;
}

.sc-enter {
  font-size: 10px;
  color: #0167CC;
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-weight: 500;
}

.sc-enter i:first-child {
  font-size: 9px;
}

.sc-enter i:last-child {
  font-size: 8px;
  opacity: .6;
}

.sc-cat-badge {
  position: absolute;
  bottom: 6px;
  left: 6px;
  font-size: 9px;
  color: #fff;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  padding: 2px 6px;
  border-radius: 6px;
  font-weight: 500;
  max-width: 80%;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

/* 推荐角标 */
.sc-recommend-badge {
  position: absolute;
  top: -5px;
  left: 5px;
  display: flex;
  align-items: center;
  z-index: 3;
  animation: recommendBadgePulse 2s ease-in-out infinite;
}

.recommend-badge-icon {
  width: auto;
  height: 30px;
  object-fit: contain;
}

@keyframes recommendBadgePulse {

  0%,
  100% {
    transform: scale(1);
    filter: drop-shadow(0 2px 4px rgba(255, 107, 53, 0.4));
  }

  50% {
    transform: scale(1.05);
    filter: drop-shadow(0 2px 8px rgba(255, 107, 53, 0.6));
  }
}

/* 优惠角标 */
.sc-coupon-badge {
  position: absolute;
  top: -5px;
  right: -5px;
  display: flex;
  align-items: center;
  animation: couponBadgePulse 2s ease-in-out infinite;
  z-index: 2;
}

.coupon-badge-icon {
  width: auto;
  height: 50px;
  object-fit: contain;
}

.sc-sale {
  font-size: 10px;
  color: #9ca3af;
  margin-top: 4px;
}

/* 商户卡片 hover 增强 */
.shop-card:hover {
  transform: translateY(-2px);
  border-color: #0167CC;
  box-shadow: 0 6px 20px rgba(1, 103, 204, 0.12);
}

.shop-card:hover .sc-enter {
  color: #004fa3;
}

.shop-card:hover .sc-enter i:last-child {
  opacity: 1;
}

/* ================================================================
   通知栏
   ================================================================ */
.notice-bar {
  background: #eff6ff;
  border-radius: 12px;
  padding: 5px 14px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border: 5px solid #fff;
}

.nb-left {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: #1f2937;
  font-weight: 500;
}

.nb-ic {
  font-size: 16px;
  color: #0167CC;
  animation: pulse 2s infinite;
  width: 30px;
}

.nb-highlight {
  color: #0167CC;
  font-weight: 600;
}

@keyframes pulse {

  0%,
  100% {
    opacity: 1;
  }

  50% {
    opacity: .5;
  }
}

.notice-bar>i {
  font-size: 10px;
  color: #93c5fd;
}

/* 固定底部公告 */
.notice-fixed {
  position: fixed;
  bottom: 48px;
  /* 底部栏高度 */
  left: 0;
  right: 0;
  padding: 0 16px;
  z-index: 39;
  pointer-events: auto;
  cursor: pointer;
}

.notice-fixed .notice-bar {
  pointer-events: auto;
  transition: transform .2s;
}

.notice-fixed:active .notice-bar {
  transform: scale(0.98);
}

/* ================================================================
   悬浮按钮
   ================================================================ */
.fab {
  position: fixed;
  right: 16px;
  bottom: 90px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  z-index: 50;
  cursor: pointer;
  transition: transform .2s;
  animation: fab-float 3s ease-in-out infinite;
}

.fab::before {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  width: 60px;
  height: 60px;
  background: radial-gradient(circle, rgba(255, 107, 53, 0.4) 0%, transparent 70%);
  border-radius: 50%;
  transform: translate(-50%, -50%);
  animation: fabGlow 2s ease-in-out infinite;
  z-index: -1;
}

@keyframes fabGlow {

  0%,
  100% {
    transform: translate(-50%, -50%) scale(1);
    opacity: 0.6;
  }

  50% {
    transform: translate(-50%, -50%) scale(1.3);
    opacity: 1;
  }
}

@keyframes fab-float {

  0%,
  100% {
    transform: translateY(0);
  }

  50% {
    transform: translateY(-8px);
  }
}

.fab:hover {
  animation: none;
  transform: scale(1.1);
}

.fab:active {
  transform: scale(.95);
  animation: none;
}

.fab-circle {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: linear-gradient(135deg, #fd271f, #e71109, #bb0701);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 22px;
  box-shadow: 0 4px 15px rgba(204, 25, 1, 0.4),
    inset 0 -2px 4px rgba(0, 0, 0, .1),
    inset 0 2px 4px rgba(255, 255, 255, .3);
  position: relative;
}

.fab-circle img {
  width: auto;
  height: 100%;
  object-fit: contain;

}

/* 脉冲光环 */
.fab-circle::before {
  content: '';
  position: absolute;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background: linear-gradient(135deg, #fd271f, #e71109, #bb0701);
  z-index: -1;
  animation: fab-pulse 2s ease-in-out infinite;
}

@keyframes fab-pulse {

  0%,
  100% {
    transform: scale(1);
    opacity: 0.6;
  }

  50% {
    transform: scale(1.4);
    opacity: 0;
  }
}

/* 礼物图标摇晃 */
.fab-circle i {
  animation: gift-shake 2.5s ease-in-out infinite;
}

@keyframes gift-shake {

  0%,
  100% {
    transform: rotate(0deg);
  }

  10% {
    transform: rotate(-15deg);
  }

  20% {
    transform: rotate(15deg);
  }

  30% {
    transform: rotate(-10deg);
  }

  40% {
    transform: rotate(10deg);
  }

  50%,
  100% {
    transform: rotate(0deg);
  }
}

/* 闪烁星星装饰 */
.fab-circle::after {
  content: '✨';
  position: absolute;
  top: -5px;
  right: -5px;
  font-size: 14px;
  animation: sparkle 1.5s ease-in-out infinite;
}

@keyframes sparkle {

  0%,
  100% {
    opacity: 1;
    transform: scale(1) rotate(0deg);
  }

  50% {
    opacity: 0.5;
    transform: scale(1.3) rotate(180deg);
  }
}

.fab-text {
  background: linear-gradient(135deg, #e71109, #bb0701);
  color: #fff;
  font-size: 11px;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(1, 103, 204, .3);
  white-space: nowrap;
  margin-top: -16px;
  animation: text-glow 2s ease-in-out infinite;
  z-index: 2;
}

@keyframes text-glow {

  0%,
  100% {
    box-shadow: 0 2px 8px rgba(1, 103, 204, .3);
  }

  50% {
    box-shadow: 0 2px 16px rgba(59, 130, 246, .6),
      0 0 20px rgba(59, 130, 204, .3);
  }
}

/* ================================================================
   底部栏
   ================================================================ */
.bottombar {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: #fff;
  border-top: 1px solid #f3f4f6;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  padding: 6px 0 calc(6px + env(safe-area-inset-bottom, 0px));
  z-index: 40;
  box-shadow: 0 -2px 8px rgba(0, 0, 0, .04);
  border-radius: 15px 15px 0 0;
}

.bb-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  color: #9ca3af;
  cursor: pointer;
  position: relative;
}

.bb-item.active {
  color: #0167CC;
}

.bb-item i {
  font-size: 18px;
}

.bb-item span {
  font-size: 10px;
  font-weight: 500;
}

.bb-item.active span {
  font-weight: 700;
}

.bb-dot {
  position: absolute;
  top: 2px;
  right: calc(50% - 14px);
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #ef4444;
}

/* ================================================================
   分享弹窗
   ================================================================ */
.share-mask {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, .5);
  z-index: 200;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.share-box {
  width: 100%;
  max-width: 320px;
  background: #fff;
  border-radius: 20px;
  padding: 28px 24px;
  text-align: center;
  position: relative;
  animation: shareIn .3s ease;
}

@keyframes shareIn {
  from {
    transform: scale(.9);
    opacity: 0;
  }

  to {
    transform: scale(1);
    opacity: 1;
  }
}

.share-close {
  position: absolute;
  top: 12px;
  right: 12px;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: #f3f4f6;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.share-close:active {
  background: #e5e7eb;
}

.share-close i {
  font-size: 12px;
  color: #6b7280;
}

.share-box h3 {
  font-size: 18px;
  font-weight: 800;
  color: #1f2937;
  margin-bottom: 6px;
}

.share-desc {
  font-size: 13px;
  color: #9ca3af;
  margin-bottom: 20px;
}

.share-qr {
  display: inline-block;
  padding: 12px;
  background: #fff;
  border: 2px solid #f3f4f6;
  border-radius: 16px;
  margin-bottom: 12px;
}

.share-qr img {
  width: 200px;
}

.share-qr canvas {
  display: block;
}

.share-url {
  font-size: 11px;
  color: #9ca3af;
  margin-bottom: 16px;
  word-break: break-all;
}

.share-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 24px;
  border: none;
  border-radius: 22px;
  background: linear-gradient(135deg, #e71109, #bb0701);
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
}

.share-btn:active {
  opacity: .85;
}

.share-btn i {
  font-size: 13px;
}

.share-btn-wechat {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 24px;
  border: none;
  border-radius: 22px;
  background: linear-gradient(135deg, #07c160, #06ad56);
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  margin-top: 10px;
}

.share-btn-wechat:active {
  opacity: .85;
}

.share-btn-wechat i {
  font-size: 16px;
}

/* ================================================================
   响应式
   ================================================================ */

/* ≤ 360px */
@media (max-width: 360px) {
  .hero {
    padding: 10px 12px 10px;
  }

  .hero-title h2 {
    font-size: 20px;
    letter-spacing: 4px;
  }

  .hero-stats {
    padding: 8px 10px;
    flex-wrap: wrap;
    gap: 4px;
  }

  .hs-item {
    font-size: 10px;
  }

  .hs-item strong {
    font-size: 12px;
  }

  .modules {
    padding: 8px 8px 16px;
  }

  .mod-card {
    padding: 10px;
  }

  .policy-card {
    min-height: 150px;
  }

  .cat-circle {
    width: 38px;
    height: 38px;
    font-size: 16px;
  }

  .sc-img {
    height: 72px;
  }

  .summer-promo-title {
    font-size: 18px;
  }

  .summer-promo-sub {
    font-size: 11px;
  }

  .summer-promo-badge {
    font-size: 10px;
    padding: 4px 10px;
  }
}

/* 481 ~ 768px */
@media (min-width: 481px) {
  .hero {
    padding: 16px 20px 14px;
  }

  .hero-title h2 {
    font-size: 28px;
  }

  .hero-stats {
    padding: 12px 16px;
  }

  .hs-item {
    font-size: 12px;
  }

  .hs-item strong {
    font-size: 16px;
  }

  .modules {
    padding: 16px;
    gap: 16px;
  }

  .mod-card {
    padding: 18px;
  }

  .mod-tt {
    font-size: 17px;
  }

  .cat-circle {
    width: 50px;
    height: 50px;
    font-size: 20px;
  }

  .goods-card {
    min-width: 128px;
  }

  .sc-img {
    height: 100px;
  }

  .summer-promo-title {
    font-size: 26px;
  }

  .summer-promo-sub {
    font-size: 14px;
  }

  .summer-promo-content {
    padding: 24px;
  }
}

/* ≥ 769px */
@media (min-width: 769px) {
  .hero {
    max-width: 600px;
    margin: 0 auto;
    border-radius: 0 0 24px 24px;
  }

  .hero-title h2 {
    font-size: 32px;
    letter-spacing: 8px;
  }

  .modules {
    max-width: 600px;
    margin: 0 auto;
  }

  .cat-circle {
    width: 56px;
    height: 56px;
    font-size: 24px;
  }

  .sc-img {
    height: 120px;
  }
}
</style>
